import * as Api from '@ViewModels';
import * as React from 'react';
import { apiModelTypeToAutomationStepType } from '../extViewmodels/sdk/Utils';

// ============================================================================
// Type Guard Functions for Automation Steps
// ============================================================================

/**
 * Get the normalized step type enum from an automation step.
 * Use this for switch statements or when you need the enum value.
 */
export function getStepType(
	step: Api.IAutomationStep | AutomationStepWithHelpers | undefined
): Api.AutomationStepType | undefined {
	return step?._type ? apiModelTypeToAutomationStepType(step._type as Api.ApiModelType) : undefined;
}

/** Type guard: checks if step is an Email step */
export function isEmailStep(step: Api.IAutomationStep | undefined): step is Api.IEmailAutomationStep {
	return step?._type === 'EmailAutomationStep';
}

/** Type guard: checks if step is a Texting step */
export function isTextingStep(step: Api.IAutomationStep | undefined): step is Api.ITextingAutomationStep {
	return step?._type === 'TextingAutomationStep';
}

/** Type guard: checks if step is a Switch step */
export function isSwitchStep(step: Api.IAutomationStep | undefined): step is Api.ISwitchAutomationStep {
	return step?._type === 'SwitchAutomationStep';
}

/** Type guard: checks if step is an AddTag step */
export function isAddTagStep(step: Api.IAutomationStep | undefined): step is Api.IAddTagAutomationStep {
	return step?._type === 'AddTagAutomationStep';
}

/** Type guard: checks if step is a RemoveTag step */
export function isRemoveTagStep(step: Api.IAutomationStep | undefined): step is Api.IRemoveTagAutomationStep {
	return step?._type === 'RemoveTagAutomationStep';
}

/** Type guard: checks if step is either AddTag or RemoveTag */
export function isTagStep(
	step: Api.IAutomationStep | undefined
): step is Api.IAddTagAutomationStep | Api.IRemoveTagAutomationStep {
	return isAddTagStep(step) || isRemoveTagStep(step);
}

/** Type guard: checks if step is an ActionItem step */
export function isActionItemStep(step: Api.IAutomationStep | undefined): step is Api.IActionItemAutomationStep {
	return step?._type === 'ActionItemAutomationStep';
}

/** Type guard: checks if step is a Handwritten Card step */
export function isHwcStep(step: Api.IAutomationStep | undefined): step is Api.HandwrittenCardAutomationStep {
	return step?._type === 'HandwrittenCardAutomationStep';
}

/** Type guard: checks if step is an HTML Newsletter step */
export function isHtmlNewsletterStep(step: Api.IAutomationStep | undefined): step is Api.IHtmlNewsletterAutomationStep {
	return step?._type === 'HtmlNewsletterAutomationStep';
}

/** Type guard: checks if step is a NoAction step */
export function isNoActionStep(step: Api.IAutomationStep | undefined): step is Api.INoActionAutomationStep {
	return step?._type === 'NoActionAutomationStep';
}

/** Type guard: checks if step is an Opportunity step */
export function isOpportunityStep(step: Api.IAutomationStep | undefined): step is Api.IOpportunityAutomationStep {
	return step?._type === 'OpportunityAutomationStep';
}

// ============================================================================
// Interfaces
// ============================================================================

export interface AutomationStepWithHelpers extends Api.IAutomationStep {
	/** Normalized step type enum - use this for comparisons instead of ._type */
	type?: Api.AutomationStepType;
	addConditionalStep?: (step: Api.IAutomationStep, outcome: Api.AutomationStepOutcome) => void;
	canEdit?: boolean;
	hasAllRequiredInfo?: boolean;
	impersonationContext?: Api.IImpersonationContext;
	isBusy?: boolean;
	model?: Api.IAutomationStep;
	removeConditionalStep?: (outcome: Api.AutomationStepOutcome) => void;
	setModel?: (updates: Partial<Api.IAutomationStep>) => void;
	showRequiredFieldError?: boolean;
	templateId?: string;
	toJs?: () => Api.IAutomationStep;
	update?: (updates: Partial<Api.IAutomationStep>) => void;
	// Switch step specific properties (from ISwitchAutomationStep)
	cases?: Api.IAutomationStepCaseStatement[];
	automationTemplateIds?: string[];
	getTemplateForCase?: (caseStatement: Api.IAutomationStepCaseStatement) => Api.IAutomationTemplate | null;
}

export interface AutomationEditorStep {
	automationStep?: AutomationStepWithHelpers;
	canEdit?: boolean;
	impersonationContext?: Api.IImpersonationContext;
	templateId?: string;
	uuid: string;
}

export interface IEditAutomationTemplateContext {
	canEdit?: boolean;
	onDraftUpdated?(): void;
	onChildTemplatesLoaded?(): void;
	parentAutomationTemplate?: Api.AutomationTemplateViewModelType;
}

export const EditAutomationTemplateContext = React.createContext<IEditAutomationTemplateContext>(null);

export enum AutomationStepAction {
	Copy = 'copy',
	Delete = 'delete',
	MoveDown = 'moveDown',
	MoveUp = 'moveUp',
}

/**
 * Interface for the automation template steps editor.
 * This interface is compatible with both the ViewModel pattern and the hook pattern.
 */
export interface IAutomationTemplateStepsEditor {
	steps: AutomationEditorStep[];
	isLoaded: boolean;
	automationTemplate: Api.AutomationTemplateViewModelType;
	children: Api.ObservableCollection<Api.AutomationStepViewModel>;

	// Additional properties for backward compatibility with ViewModel pattern
	executeActionForStep?: (step: AutomationEditorStep, action: AutomationStepAction) => Promise<void>;
	// addSegment can take either a boolean (deleteFirstEmptyStep) or ISwitchAutomationStep depending on context
	addSegment?: (segmentDataOrDeleteFirst?: Api.ISwitchAutomationStep | boolean) => Promise<void>;
	canReoderSteps?: boolean;
	mSteps?: AutomationEditorStep[];
	impersonationContext?: Api.IImpersonationContext;
	automationTemplateTrigger?: Api.IAutomationTrigger;
	loading?: boolean;
	updateStep?: (stepId: string, updater: (step: Api.IAutomationStep) => Api.IAutomationStep) => void;
	setShowRequiredFieldError?: (stepId: string, value: boolean) => void;
	setStepsState?: React.Dispatch<React.SetStateAction<AutomationEditorStep[]>>;
}

export interface IEditAutomationTemplateStepsContext {
	editorStepElementRefs?: Record<string, React.MutableRefObject<HTMLElement>>;
	editorSteps: IAutomationTemplateStepsEditor;
}

export const EditAutomationTemplateStepsContext = React.createContext<IEditAutomationTemplateStepsContext>(null);

export const EditAutomationTemplateItemIdPrefix = 'edit-automation-template-item-scroll-anchor';

export enum AutomationsReportingTableColumn {
	Communication = 'Communication',
	Policy = 'Policy',
	Recipient = 'Recipient',
	StatusAndActions = 'Status / Actions',
}
